import { TransactionHistorySkeleton } from '@/components/skeletons/WalletPageSkeletons';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Heading, Text } from '@/components/ui/typography';
import { devLog } from '@/lib/logger';
import { supabaseBrowser } from '@/lib/supabase-browser';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletTransactions } from './hooks/useWallet';
import { TransactionCard } from './TransactionCard';
import { TransactionListHeader } from './TransactionListHeader';

interface TransactionHistoryProps {
  showExportButton?: boolean;
}

export function TransactionHistory({ showExportButton = true }: TransactionHistoryProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  
  // Fetch real transactions
  const { data: transactions, isLoading, error } = useWalletTransactions({
    status: selectedStatus !== 'all' ? selectedStatus : undefined,
    type: selectedType !== 'all' ? selectedType : undefined,
  });

  // Realtime subscription for tx table updates
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabaseBrowser.auth.getUser();
      return user?.id;
    };

    let channel: ReturnType<typeof supabaseBrowser.channel> | null = null;

    getUser().then((userId) => {
      if (!userId) return;

      // Subscribe to tx table changes for this user
      channel = supabaseBrowser
        .channel(`user-transactions-${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'tx',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            devLog('[TransactionHistory] Transaction updated via realtime:', payload);
            // Invalidate queries to refetch transactions
            queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
          }
        )
        .subscribe();

      // PII log removed - subscribed to realtime updates
    });

    return () => {
      if (channel) {
        channel.unsubscribe();
        devLog('[TransactionHistory] Unsubscribed from realtime updates');
      }
    };
  }, [queryClient]);

  const filteredTransactions = (transactions || []).filter(tx => {
    const matchesSearch = tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (tx.amount?.currency || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || tx.type === selectedType;
    // Status is already filtered by RPC if passed, but double check for client side flexibility if RPC doesn't support it fully yet
    const matchesStatus = selectedStatus === 'all' || tx.status === selectedStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleExport = () => {
    // TODO: Implement export functionality
    devLog('Export transactions', filteredTransactions);
  };

  // Skeleton loading state - CANON 5.3/5.4: Shape-matching skeletons
  if (isLoading) {
    return <TransactionHistorySkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Card className={cn(
        semanticTokens.danger.border,
        semanticTokens.danger.bg,
        semanticTokens.motion.standard
      )}>
        <CardContent className={cn(
          semanticTokens.spacing.lg,
          "text-center",
          semanticTokens.danger.fg
        )}>
          <Heading level={4} className="mb-2">
            Failed to load transactions
          </Heading>
          <Text variant="md" color="muted">
            {error instanceof Error ? error.message : 'Please try again later.'}
          </Text>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={semanticTokens.motion.standard}>
      <CardContent className={cn("space-y-6", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
        {/* Header with Search & Filters */}
        <TransactionListHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedType={selectedType}
          onTypeChange={setSelectedType}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          totalCount={transactions?.length || 0}
          filteredCount={filteredTransactions.length}
          showExportButton={showExportButton}
          onExport={handleExport}
        />

        {/* Transactions Grid */}
        {filteredTransactions.length === 0 ? (
          <EmptyState
            title="No transactions found"
            description={
              searchTerm || selectedType !== 'all' || selectedStatus !== 'all'
                ? "Try adjusting your filters"
                : "No transactions yet"
            }
            variant="no-results"
          />
        ) : (
          <div className={cn(
            "grid grid-cols-1 lg:grid-cols-2 gap-4",
            semanticTokens.spacing.md.replace('p-', 'gap-')
          )}>
            {filteredTransactions.map((tx) => (
              <TransactionCard
                key={tx.id}
                transaction={tx}
                onClick={() => navigate(`/dashboard/transactions/${tx.id}`)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
